import { useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { useLanguage } from "@/hooks/use-language";
import { submitFeedback, type FeedbackCategory } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Loader2, MessageCirclePlus, Send } from "lucide-react";

const CATEGORIES: { value: FeedbackCategory; emoji: string }[] = [
  { value: "bug", emoji: "🐛" },
  { value: "idea", emoji: "💡" },
  { value: "other", emoji: "🗒" },
];

const MAX_LENGTH = 4000;

export function FeedbackButton() {
  const { t } = useLanguage();
  const pathname = useLocation({ select: (s) => s.pathname });
  const isTakingTest = /\/exams\/[^/]+\/test\/[^/]+$/.test(pathname);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>("idea");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  function reset() {
    setCategory("idea");
    setMessage("");
    setSubmitted(false);
    setError("");
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    setOpen(next);
  }

  async function handleSubmit() {
    const trimmed = message.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError("");
    try {
      await submitFeedback({ category, message: trimmed, path: pathname });
      setSubmitted(true);
    } catch (err) {
      setError(createErrorMessage(err, t("feedback.error")));
    } finally {
      setSubmitting(false);
    }
  }

  if (isTakingTest) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        aria-label={t("feedback.button")}
        onClick={() => setOpen(true)}
        className="fixed right-4 bottom-4 z-40 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-medium text-primary-foreground shadow-lg transition-all hover:scale-105 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:right-6 sm:bottom-6"
      >
        <MessageCirclePlus className="size-4" />
        <span className="hidden sm:inline">{t("feedback.button")}</span>
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCirclePlus className="size-4 text-primary" />
              {t("feedback.title")}
            </DialogTitle>
            <DialogDescription>{t("feedback.desc")}</DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="py-6 text-center">
              <p className="text-sm text-green-600 dark:text-green-400">
                {t("feedback.success")}
              </p>
              <DialogClose
                render={<Button variant="outline" className="mt-4 cursor-pointer" />}
              >
                {t("common.confirm")}
              </DialogClose>
            </div>
          ) : (
            <div className="space-y-4">
              {error && (
                <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {t("feedback.category_label")}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORIES.map((c) => (
                    <Button
                      key={c.value}
                      type="button"
                      variant={category === c.value ? "default" : "outline"}
                      size="sm"
                      className="cursor-pointer text-xs"
                      onClick={() => setCategory(c.value)}
                    >
                      <span className="mr-1">{c.emoji}</span>
                      {t(`feedback.category_${c.value}`)}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="block text-sm font-medium">
                    {t("feedback.message_label")}
                  </label>
                  <span className="text-xs text-muted-foreground">
                    {message.length}/{MAX_LENGTH}
                  </span>
                </div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
                  rows={5}
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                  placeholder={t("feedback.message_placeholder")}
                  disabled={submitting}
                />
              </div>
              <DialogFooter>
                <DialogClose
                  render={<Button variant="outline" className="cursor-pointer" />}
                >
                  {t("common.cancel")}
                </DialogClose>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !message.trim()}
                  className="cursor-pointer"
                >
                  {submitting ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 size-4" />
                  )}
                  {t("feedback.submit")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
